/** Caption/paragraph windowing registry (SPP-654). Document stays intact; paint gated via content-visibility. */

import { findCaptionBlockForTime } from 'utils/transcriptChunk.util';
import { visibilityLog } from './chunkDebugLog';
import { transcriptChunkStore } from './TranscriptChunkStore';

export type VisibilityReason =
  | 'search'
  | 'playback'
  | 'mode-reset'
  | 'manual'
  | 'selection'
  | 'observe';

export type TranscriptVisibilityApi = {
  enabled: boolean;
  overscanPx: number;
  isHeavyMounted: (key: string) => boolean;
  observe: (el: Element, key: string) => () => void;
  pin: (key: string, reason?: VisibilityReason) => void;
  unpin: (key: string) => void;
  ensureVisible: (
    key: string,
    reason?: VisibilityReason,
  ) => Promise<boolean>;
  reset: () => void;
  setEnabled: (enabled: boolean) => void;
  setOverscanPx: (px: number) => void;
  setScrollRoot: (root: Element | null) => void;
  /** Prefer keyed subscribe so scroll IO does not setState every row. */
  subscribe: (key: string, listener: () => void) => () => void;
};

const VIEW_OVERSCAN_PX = 8 * 80;
const EDIT_OVERSCAN_PX = 16 * 80;
const ENSURE_VISIBLE_TIMEOUT_MS = 2000;
const ENSURE_VISIBLE_POLL_MS = 32;

const keyListeners = new Map<string, Set<() => void>>();
const visibleKeys = new Set<string>();
const pinnedKeys = new Set<string>();
const observed = new Map<Element, string>();

let enabled = true;
let overscanPx = VIEW_OVERSCAN_PX;
let scrollRoot: Element | null = null;
let observer: IntersectionObserver | null = null;
let playbackPinKey: string | null = null;
let searchPinKey: string | null = null;
let selectionPinKey: string | null = null;
let notifyRaf = 0;
const pendingNotifyKeys = new Set<string>();

const snapshotVisibility = () => ({
  enabled,
  overscanPx,
  scrollRoot: scrollRoot
    ? (scrollRoot as HTMLElement).tagName +
      ((scrollRoot as HTMLElement).className
        ? `.${String((scrollRoot as HTMLElement).className).split(' ')[0]}`
        : '')
    : null,
  observedCount: observed.size,
  visibleCount: visibleKeys.size,
  pinnedCount: pinnedKeys.size,
  listenerKeys: keyListeners.size,
  pins: {
    playback: playbackPinKey,
    search: searchPinKey,
    selection: selectionPinKey,
  },
  visibleKeys: Array.from(visibleKeys).slice(0, 40),
  pinnedKeys: Array.from(pinnedKeys),
});

const logVisibility = (label: string, extra?: Record<string, unknown>) => {
  visibilityLog(label, {
    ...snapshotVisibility(),
    ...(extra || {}),
  });
};

/** Dump current registry state from DevTools (`window.dumpTranscriptVisibility()`). */
export const dumpTranscriptVisibility = () => {
  const snap = snapshotVisibility();
  visibilityLog('dump', snap);
  return snap;
};

if (typeof window !== 'undefined') {
  (window as any).dumpTranscriptVisibility = dumpTranscriptVisibility;
}

const flushNotify = () => {
  notifyRaf = 0;
  const keys = Array.from(pendingNotifyKeys);
  pendingNotifyKeys.clear();
  for (const key of keys) {
    const set = keyListeners.get(key);
    if (!set) continue;
    set.forEach((l) => l());
  }
};

/** Coalesce IO storms to one animation frame; only wake subscribed keys.
 *  Playback/selection pins flush immediately so word highlight is not a frame late. */
const notifyKeys = (keys: string[], immediate = false) => {
  for (let i = 0; i < keys.length; i++) {
    pendingNotifyKeys.add(keys[i]);
  }
  if (immediate) {
    if (notifyRaf) {
      cancelAnimationFrame(notifyRaf);
      notifyRaf = 0;
    }
    flushNotify();
    return;
  }
  if (!notifyRaf) {
    notifyRaf = requestAnimationFrame(flushNotify);
  }
};

const rebuildObserver = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  if (typeof IntersectionObserver === 'undefined') {
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      const changed = new Set<string>();
      for (const entry of entries) {
        const key = observed.get(entry.target);
        if (!key) continue;
        if (entry.isIntersecting) {
          if (!visibleKeys.has(key)) {
            visibleKeys.add(key);
            changed.add(key);
          }
        } else if (!pinnedKeys.has(key) && visibleKeys.has(key)) {
          visibleKeys.delete(key);
          changed.add(key);
        }
      }
      if (changed.size) {
        logVisibility('io-change', {
          changed: Array.from(changed).slice(0, 20),
          changedCount: changed.size,
        });
        notifyKeys(Array.from(changed));
      }
    },
    {
      root: scrollRoot,
      rootMargin: `${overscanPx}px 0px ${overscanPx}px 0px`,
      threshold: 0,
    },
  );

  observed.forEach((_key, el) => {
    observer?.observe(el);
  });
  logVisibility('rebuildObserver');
};

export const transcriptVisibilityKey = (start: number | string | undefined) =>
  String(start ?? '');

export const captionStartForTime = (time: number): string => {
  if (transcriptChunkStore.isEnabled()) {
    const resolved = transcriptChunkStore.resolvePlaybackCaptionAtTime(time);
    if (resolved) return transcriptVisibilityKey(resolved.captionStart);
  }

  const children = (window as any)?.Editor?.children as
    | { data?: { start?: number; end?: number } }[]
    | undefined;
  if (!children?.length || time == null) {
    return transcriptVisibilityKey(time);
  }
  const block = findCaptionBlockForTime(children, time);
  return transcriptVisibilityKey(block?.data?.start ?? time);
};

export const transcriptVisibility: TranscriptVisibilityApi = {
  get enabled() {
    return enabled;
  },
  get overscanPx() {
    return overscanPx;
  },

  isHeavyMounted(key: string) {
    if (!enabled) return true;
    if (!key) return true;
    return pinnedKeys.has(key) || visibleKeys.has(key);
  },

  observe(el: Element, key: string) {
    if (!key || !el) {
      return () => undefined;
    }

    observed.set(el, key);
    if (!observer) {
      rebuildObserver();
    } else {
      observer.observe(el);
    }

    return () => {
      observed.delete(el);
      observer?.unobserve(el);
      let stillObserved = false;
      observed.forEach((k) => {
        if (k === key) stillObserved = true;
      });
      if (!stillObserved && !pinnedKeys.has(key)) {
        if (visibleKeys.delete(key)) {
          notifyKeys([key]);
        }
      }
    };
  },

  pin(key: string, reason?: VisibilityReason) {
    if (!key) return;

    if (reason === 'playback') {
      if (playbackPinKey && playbackPinKey !== key) {
        pinnedKeys.delete(playbackPinKey);
        notifyKeys([playbackPinKey], true);
      }
      playbackPinKey = key;
    }
    if (reason === 'search') {
      if (searchPinKey && searchPinKey !== key) {
        pinnedKeys.delete(searchPinKey);
        notifyKeys([searchPinKey]);
      }
      searchPinKey = key;
    }
    if (reason === 'selection') {
      if (selectionPinKey && selectionPinKey !== key) {
        pinnedKeys.delete(selectionPinKey);
        notifyKeys([selectionPinKey], true);
      }
      selectionPinKey = key;
    }

    // Search jumps must wake the target row before scroll/DOM query.
    const urgent =
      reason === 'selection' ||
      reason === 'playback' ||
      reason === 'search';
    if (!pinnedKeys.has(key)) {
      pinnedKeys.add(key);
      visibleKeys.add(key);
      notifyKeys([key], urgent);
    } else if (!visibleKeys.has(key)) {
      visibleKeys.add(key);
      notifyKeys([key], urgent);
    }
    logVisibility('pin', { key, reason, urgent });
  },

  unpin(key: string) {
    if (!key || !pinnedKeys.has(key)) return;
    pinnedKeys.delete(key);
    if (playbackPinKey === key) playbackPinKey = null;
    if (searchPinKey === key) searchPinKey = null;
    if (selectionPinKey === key) selectionPinKey = null;
    notifyKeys([key]);
    logVisibility('unpin', { key });
  },

  async ensureVisible(key: string, reason?: VisibilityReason) {
    if (!key) return true;
    const t0 = performance.now();
    transcriptVisibility.pin(key, reason ?? 'manual');

    // Already painted — center the caption row in the transcript pane.
    if (transcriptVisibility.isHeavyMounted(key)) {
      const shell = getCaptionShellElement(key);
      if (shell) {
        centerElementInTranscriptScrollRoot(shell, 'auto');
      }
      logVisibility('ensureVisible:fast', {
        key,
        reason,
        ms: +(performance.now() - t0).toFixed(2),
        hasShell: !!shell,
      });
      return true;
    }

    const shell = getCaptionShellElement(key);
    if (shell) {
      centerElementInTranscriptScrollRoot(shell, 'auto');
    }

    if (!enabled) {
      logVisibility('ensureVisible:disabled', { key, reason });
      return true;
    }

    const started = Date.now();
    while (Date.now() - started < ENSURE_VISIBLE_TIMEOUT_MS) {
      if (transcriptVisibility.isHeavyMounted(key)) {
        await new Promise((r) =>
          requestAnimationFrame(() => requestAnimationFrame(r)),
        );
        logVisibility('ensureVisible:ok', {
          key,
          reason,
          ms: +(performance.now() - t0).toFixed(2),
        });
        return true;
      }
      await new Promise((r) => setTimeout(r, ENSURE_VISIBLE_POLL_MS));
    }
    const ok = transcriptVisibility.isHeavyMounted(key);
    logVisibility('ensureVisible:timeout', {
      key,
      reason,
      ok,
      ms: +(performance.now() - t0).toFixed(2),
    });
    return ok;
  },

  reset() {
    visibleKeys.clear();
    pinnedKeys.clear();
    playbackPinKey = null;
    searchPinKey = null;
    selectionPinKey = null;
    rebuildObserver();
    // Wake subscribers once; do NOT force layout on every row (freezes large docs).
    keyListeners.forEach((_set, key) => pendingNotifyKeys.add(key));
    if (!notifyRaf) {
      notifyRaf = requestAnimationFrame(flushNotify);
    }
    logVisibility('reset');
  },


  setEnabled(next: boolean) {
    if (enabled === next) return;
    enabled = next;
    rebuildObserver();
    // When disabling, isHeavyMounted short-circuits — do not wake every row
    // (that froze large files on play). When re-enabling, refresh paint gates.
    if (next) {
      keyListeners.forEach((_set, key) => pendingNotifyKeys.add(key));
      if (!notifyRaf) {
        notifyRaf = requestAnimationFrame(flushNotify);
      }
    }
    logVisibility('setEnabled', { next });
  },

  setOverscanPx(px: number) {
    if (overscanPx === px) return;
    overscanPx = px;
    rebuildObserver();
    logVisibility('setOverscanPx', { px });
  },

  setScrollRoot(root: Element | null) {
    // Same root (including repeated calls from each Transcript row mount) — no-op.
    if (scrollRoot === root) return;
    scrollRoot = root;
    rebuildObserver();
    logVisibility('setScrollRoot', { hasRoot: !!root });
  },

  subscribe(key: string, listener: () => void) {
    if (!key) {
      return () => undefined;
    }
    let set = keyListeners.get(key);
    if (!set) {
      set = new Set();
      keyListeners.set(key, set);
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) {
        keyListeners.delete(key);
      }
    };
  },
};

export const VIEW_MODE_OVERSCAN_PX = VIEW_OVERSCAN_PX;
export const EDIT_MODE_OVERSCAN_PX = EDIT_OVERSCAN_PX;

export const resolveTranscriptScrollRoot = (
  fromEl?: Element | null,
): Element | null => {
  if (fromEl) {
    const closest = fromEl.closest('[data-transcript-scroll-root]');
    if (closest) return closest;
  }
  return window.document.querySelector('[data-transcript-scroll-root]');
};

export const getCaptionShellElement = (
  blockKey: string,
): HTMLElement | null => {
  if (!blockKey) return null;
  return window.document.querySelector(
    `div[data-slate-node="element"][data-start="${blockKey}"]`,
  ) as HTMLElement | null;
};

/** Center an element inside the transcript scroll root (avoid scrolling the page). */
export const centerElementInTranscriptScrollRoot = (
  el: HTMLElement,
  behavior: ScrollBehavior = 'auto',
) => {
  const root = resolveTranscriptScrollRoot(el) as HTMLElement | null;
  if (root && root.contains(el)) {
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const delta =
      elRect.top + elRect.height / 2 - (rootRect.top + rootRect.height / 2);
    transcriptChunkStore.markProgrammaticScroll(
      behavior === 'smooth' ? 400 : 250,
    );
    if (behavior === 'smooth') {
      root.scrollBy({ top: delta, behavior: 'smooth' });
    } else {
      root.scrollTop += delta;
    }
    return;
  }
  el.scrollIntoView({ block: 'center', behavior });
};

/** Scroll the caption only if it is outside the pane — never center (edit tools). */
export const keepCaptionNearestInTranscriptViewport = (
  captionStart: number | string | undefined,
) => {
  const key = transcriptVisibilityKey(captionStart);
  const shell = getCaptionShellElement(key);
  const root = resolveTranscriptScrollRoot(shell) as HTMLElement | null;
  if (!shell || !root || !root.contains(shell)) return;

  const rootRect = root.getBoundingClientRect();
  const elRect = shell.getBoundingClientRect();
  const inView = elRect.bottom > rootRect.top && elRect.top < rootRect.bottom;
  if (inView) return;

  let delta = 0;
  if (elRect.top < rootRect.top) delta = elRect.top - rootRect.top;
  else if (elRect.bottom > rootRect.bottom) delta = elRect.bottom - rootRect.bottom;
  if (Math.abs(delta) < 2) return;

  transcriptChunkStore.markProgrammaticScroll(250);
  root.scrollTop += delta;
};

/**
 * Split / merge / add must not yank the pane. Freeze scrollTop around the
 * mutation, then bring the new row into view only if it left the viewport.
 */
export const runStructuralTranscriptEdit = (
  fn: () => number | string | undefined | void,
) => {
  const root = resolveTranscriptScrollRoot() as HTMLElement | null;
  const top = root?.scrollTop ?? 0;
  let focusStart: number | string | undefined;
  try {
    const result = fn();
    if (result != null && result !== '') focusStart = result;
  } finally {
    const restoreAndKeep = () => {
      if (root && Math.abs(root.scrollTop - top) > 1) {
        transcriptChunkStore.markProgrammaticScroll(250);
        root.scrollTop = top;
      }
      if (focusStart != null && focusStart !== '') {
        keepCaptionNearestInTranscriptViewport(focusStart);
      }
    };
    restoreAndKeep();
    requestAnimationFrame(() => {
      restoreAndKeep();
      requestAnimationFrame(restoreAndKeep);
    });
  }
};

export const isElementCenteredInTranscriptRoot = (
  el: HTMLElement,
  toleranceRatio = 0.06,
): boolean => {
  const root = resolveTranscriptScrollRoot(el) as HTMLElement | null;
  if (!root) return true;
  const rootRect = root.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const elCenter = elRect.top + elRect.height / 2;
  const rootCenter = rootRect.top + rootRect.height / 2;
  const tolerance = Math.max(20, rootRect.height * toleranceRatio);
  return Math.abs(elCenter - rootCenter) <= tolerance;
};
